import { createContext, useState, useEffect, useCallback, useRef } from 'react'
import { jwtDecode } from 'jwt-decode'
import axiosClient from '@/api/axiosClient'
import { AUTH_ENDPOINTS } from '@/api/endpoints'
import { TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY } from '@/utils/constants'
import toast from 'react-hot-toast'

export const AuthContext = createContext(null)

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const hasLoadedRef = useRef(false)

  // Check if token is expired
  const isTokenExpired = useCallback(token => {
    if (!token) return true

    try {
      const decoded = jwtDecode(token)
      return decoded.exp * 1000 < Date.now()
    } catch {
      return true
    }
  }, [])

  // Load user from token
  const loadUserFromToken = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY)

    if (!token || isTokenExpired(token)) {
      setIsLoading(false)
      return
    }

    // Si on vient juste de se connecter, ne pas appeler /me
    // car les infos utilisateur sont déjà disponibles dans le localStorage
    const isJustLoggedIn = localStorage.getItem('_just_logged_in') === 'true'
    const savedUser = localStorage.getItem(USER_KEY)
    
    if (isJustLoggedIn && savedUser) {
      try {
        const userData = JSON.parse(savedUser)
        setUser(userData)
        setIsAuthenticated(true)
        localStorage.removeItem('_just_logged_in')
        setIsLoading(false)
        return
      } catch (e) {
        // Fallback si parsing échoue
      }
    }

    try {
      const decoded = jwtDecode(token)
      const response = await axiosClient.get(AUTH_ENDPOINTS.PROFILE)
      
      // ✅ Backend returns StandardApiResponse, so data is in response.data.data
      const userData = response.data.data || response.data
      
      setUser(userData)
      setIsAuthenticated(true)
      // Mettre à jour localStorage avec les données complètes du serveur
      localStorage.setItem(USER_KEY, JSON.stringify(userData))
    } catch (error) {
      console.error('Failed to load user:', error)
      // Si on a un utilisateur sauvegardé et qu'on vient de se connecter, 
      // ne pas nettoyer les tokens car c'est probablement un problème temporaire
      const isJustLoggedIn = localStorage.getItem('_just_logged_in') === 'true'
      const savedUser = localStorage.getItem(USER_KEY)
      
      if (isJustLoggedIn && savedUser) {
        // Utiliser les données sauvegardées
        try {
          const userData = JSON.parse(savedUser)
          setUser(userData)
          setIsAuthenticated(true)
          localStorage.removeItem('_just_logged_in')
          setIsLoading(false)
          return
        } catch (e) {
          // Si parsing échoue, nettoyer quand même
        }
      }
      
      // Sinon, nettoyer les tokens
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      localStorage.removeItem(USER_KEY)
      localStorage.removeItem('_just_logged_in')
      // Don't redirect here, let ProtectedRoute handle it
    } finally {
      setIsLoading(false)
      localStorage.removeItem('_just_logged_in')
    }
  }, [isTokenExpired])

  // Initialize auth state on mount - seulement une fois
  useEffect(() => {
    // Éviter les appels multiples en vérifiant le flag
    if (hasLoadedRef.current) {
      return
    }
    hasLoadedRef.current = true
    
    loadUserFromToken()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Tableau vide pour n'exécuter qu'une seule fois au montage

  // Login
  const login = async (emailOrUsername, password, rememberMe = false) => {
    try {
      // Backend expects 'username' field (can accept email or username)
      const response = await axiosClient.post(AUTH_ENDPOINTS.LOGIN, {
        username: emailOrUsername, // Send as 'username' field
        password,
      })

      // Backend returns StandardApiResponse<AuthResponse>, so data is in response.data.data
      const authData = response.data.data || response.data
      const { accessToken, refreshToken, username: responseUsername, roles } = authData

      // Build user object from response
      const user = {
        username: responseUsername,
        roles: roles || [],
      }

      // Store tokens
      localStorage.setItem(TOKEN_KEY, accessToken)
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
      }
      localStorage.setItem(USER_KEY, JSON.stringify(user))

      // Update state
      setUser(user)
      setIsAuthenticated(true)
      
      // Flag temporaire pour éviter l'appel à /me juste après (via localStorage)
      localStorage.setItem('_just_logged_in', 'true')
      setTimeout(() => localStorage.removeItem('_just_logged_in'), 1000)

      toast.success('Connexion réussie')
      return { success: true, user }
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Échec de la connexion'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  // Register
  const register = async (userData) => {
    try {
      // Transform frontend fields to backend format
      // Backend expects: username (required), email (required), password (required), prenom (optional), nom (optional)
      // Frontend sends: firstName, lastName, email, password, confirmPassword, acceptTerms
      
      // Generate username from email (before @) or from firstName+lastName
      // Backend requires: min 3, max 50 characters
      const generateUsername = () => {
        let username = ''
        
        if (userData.firstName && userData.lastName) {
          // Use firstName.lastName format (lowercase, remove spaces/special chars)
          username = `${userData.firstName}.${userData.lastName}`.toLowerCase().replace(/[^a-z0-9.]/g, '')
        } else {
          // Fallback to email prefix
          username = userData.email.split('@')[0].toLowerCase().replace(/[^a-z0-9.]/g, '')
        }
        
        // Ensure minimum length (add random suffix if needed)
        if (username.length < 3) {
          const randomSuffix = Math.random().toString(36).substring(2, 5)
          username = username + randomSuffix
        }
        
        // Truncate if too long (max 50)
        if (username.length > 50) {
          username = username.substring(0, 50)
        }
        
        return username
      }
      
      const registerData = {
        username: generateUsername(), // Required: min 3, max 50 characters
        email: userData.email, // Required
        password: userData.password, // Required: min 6 characters
        prenom: userData.firstName || undefined, // Optional
        nom: userData.lastName || undefined, // Optional
        // roles is optional, backend will assign default ROLE_EMPLOYE if not provided
      }
      
      const response = await axiosClient.post(AUTH_ENDPOINTS.REGISTER, registerData)

      // Backend returns StandardApiResponse<AuthResponse>, so data is in response.data.data
      const authData = response.data.data || response.data
      const { accessToken, refreshToken, username, roles } = authData

      // Build user object from response
      const user = {
        username,
        roles: roles || [],
      }

      // Store tokens
      localStorage.setItem(TOKEN_KEY, accessToken)
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
      }
      localStorage.setItem(USER_KEY, JSON.stringify(user))

      // Update state
      setUser(user)
      setIsAuthenticated(true)

      toast.success('Inscription réussie')
      return { success: true, user }
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Échec de l\'inscription'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  // Logout
  const logout = useCallback(async () => {
    try {
      await axiosClient.post(AUTH_ENDPOINTS.LOGOUT)
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Clear storage and state
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      localStorage.removeItem(USER_KEY)
      setUser(null)
      setIsAuthenticated(false)
      toast.success('Déconnexion réussie')
    }
  }, [])

  // Update profile
  const updateProfile = async (profileData) => {
    try {
      const response = await axiosClient.put(AUTH_ENDPOINTS.UPDATE_PROFILE, profileData)
      
      // ✅ Backend returns StandardApiResponse, so data is in response.data.data
      const userData = response.data.data || response.data
      
      setUser(userData)
      localStorage.setItem(USER_KEY, JSON.stringify(userData))
      toast.success('Profil mis à jour')
      return { success: true, user: userData }
    } catch (error) {
      const message = error.message || 'Échec de la mise à jour du profil'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axiosClient.post(AUTH_ENDPOINTS.CHANGE_PASSWORD, {
        currentPassword,
        newPassword,
      })
      toast.success('Mot de passe changé avec succès')
      return { success: true }
    } catch (error) {
      const message = error.message || 'Échec du changement de mot de passe'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  // Get user
  const getUser = useCallback(() => {
    return user
  }, [user])

  // Check if user has role
  const hasRole = useCallback(
    (role) => {
      if (!user || !user.roles) return false
      return user.roles.includes(role)
    },
    [user]
  )

  // Check if user has permission
  const hasPermission = useCallback(
    (permission) => {
      if (!user || !user.permissions) return false
      return user.permissions.includes(permission)
    },
    [user]
  )

  const value = {
    user,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    getUser,
    hasRole,
    hasPermission,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

